import { useState, useMemo, useCallback, useEffect } from "react";
import { Outlet, useRouteLoaderData, useRevalidator } from "react-router";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { WorkspaceSidebar } from "@/components/WorkspaceSidebar";
import { SettingsDialog } from "@/components/SettingsDialog";
import { CreateDatasetDialog } from "@/components/CreateDatasetDialog";
import { ErrorLogButton } from "@/components/ErrorLogButton";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
	WORKSPACE_LOADER_ID,
	type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import { useWorkspaceSidebarActions } from "@/hooks/useWorkspaceSidebarActions";
import type { WorkspaceOutletContext } from "@/hooks/useWorkspaceViewContext";

const Workspace = () => {
	const { projects, judges, codes, datasets, profile, userIsAdmin } =
		useRouteLoaderData(WORKSPACE_LOADER_ID) as WorkspaceLoaderData;
	const user = useAuthenticatedUser();
	const userDisplayName =
		profile?.display_name ??
		user.user_metadata?.["full_name"] ??
		user.user_metadata?.["name"] ??
		user.email ??
		"Unknown";
	const revalidator = useRevalidator();
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [createDatasetDialogOpen, setCreateDatasetDialogOpen] = useState(false);
	const {
		createProject: submitCreateProject,
		createJudge: submitCreateJudge,
		createCode: submitCreateCode,
		createDataset: submitCreateDataset,
		updateProjectTitle: submitUpdateProjectTitle,
		updateJudgeTitle: submitUpdateJudgeTitle,
		updateCodeTitle: submitUpdateCodeTitle,
	} = useWorkspaceSidebarActions();

	const refreshWorkspaceData = useCallback(() => {
		revalidator.revalidate();
	}, [revalidator]);

	const createNewProject = useCallback(() => {
		submitCreateProject();
	}, [submitCreateProject]);

	const createNewJudge = useCallback(() => {
		submitCreateJudge();
	}, [submitCreateJudge]);

	const createNewCode = useCallback(() => {
		submitCreateCode();
	}, [submitCreateCode]);

	const createNewDataset = useCallback(() => {
		setCreateDatasetDialogOpen(true);
	}, []);

	const handleCreateDataset = useCallback(
		(name: string, datasetKind: "behavior_examples" | "operational_rules") => {
			submitCreateDataset({ name, dataset_kind: datasetKind });
		},
		[submitCreateDataset],
	);

	const updateProjectTitle = useCallback(
		(id: string, title: string) => {
			submitUpdateProjectTitle(id, title);
		},
		[submitUpdateProjectTitle],
	);

	const updateJudgeTitle = useCallback(
		(id: string, title: string) => {
			submitUpdateJudgeTitle(id, title);
		},
		[submitUpdateJudgeTitle],
	);

	const updateCodeTitle = useCallback(
		(id: string, title: string) => {
			submitUpdateCodeTitle(id, title);
		},
		[submitUpdateCodeTitle],
	);

	useEffect(() => {
		console.info("[Workspace] Current user", {
			id: user.id,
			name: userDisplayName,
			email: user.email ?? "Unknown",
			isAdmin: userIsAdmin,
		});
	}, [user.id, userDisplayName, user.email, userIsAdmin]);

	const contextValue = useMemo<WorkspaceOutletContext>(
		() => ({
			projects,
			judges,
			codes,
			datasets,
			profile,
			user,
			userIsAdmin,
			refreshWorkspaceData,
			createProject: createNewProject,
			createJudge: createNewJudge,
			createCode: createNewCode,
			createDataset: createNewDataset,
		}),
		[
			projects,
			judges,
			codes,
			datasets,
			profile,
			user,
			userIsAdmin,
			refreshWorkspaceData,
			createNewProject,
			createNewJudge,
			createNewCode,
			createNewDataset,
		],
	);

	return (
		<SidebarProvider>
			<div className="flex min-h-screen w-full">
				<WorkspaceSidebar
					projects={projects}
					judges={judges}
					codes={codes}
					datasets={datasets}
					onNewProject={createNewProject}
					onNewJudge={createNewJudge}
					onNewCode={createNewCode}
					onNewDataset={createNewDataset}
					onUpdateProjectTitle={updateProjectTitle}
					onUpdateJudgeTitle={updateJudgeTitle}
					onUpdateCodeTitle={updateCodeTitle}
					onOpenSettings={() => setSettingsOpen(true)}
				/>
				<main className="flex-1">
					<div className="border-b border-border p-2 flex items-center justify-between">
						<SidebarTrigger />
						<ErrorLogButton />
					</div>
					<Outlet context={contextValue} />
				</main>
				<SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
				<CreateDatasetDialog
					open={createDatasetDialogOpen}
					onOpenChange={setCreateDatasetDialogOpen}
					onSubmit={handleCreateDataset}
				/>
			</div>
		</SidebarProvider>
	);
};

export default Workspace;
